import AiStudioMenuIcon from 'assets/images/others/ai-studio.svg';
import DamMenuIcon from 'assets/images/others/dam.svg';
import ConfiguratorMenuIcon from 'assets/images/others/product-configurator.svg';
import CustomizerMenuIcon from 'assets/images/others/visual-customizer.svg';
import React, { FunctionComponent, useRef } from 'react';
import { useAppState } from 'src/shared/appState';
import { useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import { T } from 'src/shared/helpers';
import { EnvType } from 'src/shared/hooks';
import AngleDown from 'src/shared/svg/angleDown';
import { Icon } from '../icon';
import { AiTag, EarlyAccessTag, NewTag } from '../tag';
import {
    ArrowToolContainer,
    ContainerText,
    IconMenuContainer,
    MenuItem,
    MenuItemIcon,
    MenuItemStrong,
    MenuLeft,
    NoWrap,
    PopupMenuItemLarge,
    PopupMenuItemLargeIcon,
    SeparatorMenu,
    ToolDropdownPortal
} from './globalsToolbar';
import SocialMenu from './socialMenu';
import { getTools } from './toolbar';
interface LeftMenuProps {
    isMenuHidden: boolean;
    forceTools: boolean;
    userInfo: any;
    ecommerceSettings: any;
    closeAll: () => void;
    setIsToolPopupVisible: (visible: boolean) => void;
    setIsSocialMenuVisible: (visible: boolean) => void;
    isSocialMenuVisible: boolean;
    isToolPopupVisible: boolean;
    handlerOnClick: (link: string, type: EnvType) => void;
    goToBlog: () => void;
    setLastUsedTool: (type: EnvType) => void;
    userID?: number;
    toolsArray?: ReturnType<typeof getTools>;
}

const LeftMenu: FunctionComponent<LeftMenuProps> = ({
    isMenuHidden,
    forceTools,
    userInfo,
    ecommerceSettings,
    closeAll,
    setIsToolPopupVisible,
    setIsSocialMenuVisible,
    isSocialMenuVisible,
    isToolPopupVisible,
    handlerOnClick,
    goToBlog,
    userID,
    toolsArray
}) => {
    const toolMenuRef = useRef<HTMLDivElement>(null);
    const { state } = useAppState();

    const isNewConfiguratorBackoffice = useFeatureFlagWithConditions('newConfiguratorBackoffice', {
        userId: (userID || 0).toString()
    });

    // Use the passed toolsArray or fallback to default tools
    const currentTools = toolsArray || getTools(isNewConfiguratorBackoffice);

    // The active tool is derived from the current URL — same signal the sidebar already uses
    // (see getSelectedSubMenu in AppLeftSidebar). The exception is CUSTOMIZER vs CONFIGURATOR_2:
    // they share the same URL space (Dashboard, products, etc.) and only differ by the persisted
    // lastUsedTool, so when the FF is on we consult state to disambiguate.
    const getActiveToolType = () => {
        const url = window.location.href.toLowerCase();
        if (url.indexOf('landings/configurator') !== -1 || url.indexOf('/composer') !== -1) {
            return 'CONFIGURATOR';
        }
        if (url.indexOf('landings/viewer') !== -1 || url.indexOf('admin/dam') !== -1) {
            return 'VIEWER';
        }
        // FF on: state=2 means the user explicitly picked "PC (backoffice unico)" from the
        // switcher. There's no URL pattern that distinguishes it from plain Customizer pages,
        // so this is the only signal we have. All other state values (1, 3, 4, 0) collapse to
        // CUSTOMIZER on these URLs.
        if (isNewConfiguratorBackoffice && state.lastUsedTool === 2) {
            return 'CONFIGURATOR_2';
        }
        return 'CUSTOMIZER';
    };

    const activeToolType = getActiveToolType();

    const renderToolIcon = (toolType: string) => {
        const tool = currentTools.find((t) => t.type === toolType);

        switch (toolType) {
            case 'CONFIGURATOR':
                return (
                    <>
                        <IconMenuContainer>
                            <Icon>
                                <ConfiguratorMenuIcon />
                            </Icon>
                        </IconMenuContainer>
                        <ContainerText data-testid='toolbar-active-tool-configurator' data-track='tool'>
                            {tool?.name}
                        </ContainerText>
                    </>
                );
            case 'VIEWER':
                return (
                    <>
                        <IconMenuContainer>
                            <Icon>
                                <DamMenuIcon />
                            </Icon>
                        </IconMenuContainer>
                        <ContainerText data-testid='toolbar-active-tool-viewer' data-track='tool'>
                            {tool?.name}
                        </ContainerText>
                    </>
                );
            case 'AI_STUDIO':
                return (
                    <>
                        <IconMenuContainer>
                            <Icon>
                                <AiStudioMenuIcon />
                            </Icon>
                        </IconMenuContainer>
                        <ContainerText data-testid='toolbar-active-tool-ai-agents' data-track='tool'>
                            {tool?.name}
                        </ContainerText>
                    </>
                );
            case 'CONFIGURATOR_2':
                return (
                    <>
                        <IconMenuContainer>
                            <Icon>
                                <ConfiguratorMenuIcon />
                            </Icon>
                        </IconMenuContainer>
                        <ContainerText data-testid='toolbar-active-tool-configurator-2' data-track='tool'>
                            {tool?.name}
                        </ContainerText>
                    </>
                );
            case 'CUSTOMIZER':
            default:
                return (
                    <>
                        <IconMenuContainer>
                            <Icon>
                                <CustomizerMenuIcon />
                            </Icon>
                        </IconMenuContainer>
                        <ContainerText data-testid='toolbar-active-tool-customizer' data-track='tool'>
                            {tool?.name}
                        </ContainerText>
                    </>
                );
        }
    };
    return (
        <MenuLeft>
            {!isMenuHidden && !forceTools && userInfo?.userID && userInfo.name && (
                <>
                    <MenuItem ref={toolMenuRef} onClick={(e) => e.nativeEvent.stopImmediatePropagation()}>
                        <NoWrap
                            data-testid='toolbar-active-tool'
                            $padding='0 5px'
                            onClick={() => {
                                closeAll();
                                setIsToolPopupVisible(true);
                            }}
                        >
                            {ecommerceSettings?.ecommerceName.toUpperCase() != 'WIX' && renderToolIcon(activeToolType)}
                            {ecommerceSettings?.ecommerceName.toUpperCase() == 'WIX' && activeToolType !== 'CONFIGURATOR' && renderToolIcon(activeToolType)}
                        </NoWrap>{' '}
                        <MenuItemIcon
                            onClick={() => {
                                closeAll();
                                setIsToolPopupVisible(true);
                            }}
                        >
                            <ArrowToolContainer>
                                <AngleDown />
                            </ArrowToolContainer>
                        </MenuItemIcon>
                        <ToolDropdownPortal
                            isVisible={isToolPopupVisible}
                            triggerRef={toolMenuRef}
                            $width={400}
                            $align={'left'}
                            onClose={() => setIsToolPopupVisible(false)}
                        >
                            {currentTools.map((tool) => {
                                if (ecommerceSettings?.ecommerceName.toUpperCase() == 'WIX' && tool.type === 'CONFIGURATOR') {
                                    return;
                                }

                                return (
                                    <PopupMenuItemLarge
                                        key={tool.type}
                                        onClick={(e) => {
                                            console.log('🔥 Tool clicked:', tool.type, tool.url);
                                            handlerOnClick(tool.url, tool.type as EnvType);
                                            e.preventDefault();
                                            e.stopPropagation();
                                            // Prima esegui l'azione
                                            // Poi chiudi il dropdown con un piccolo delay
                                            setTimeout(() => {
                                                closeAll();
                                            }, 100);
                                        }}
                                        data-testid={`tool-option-${tool.type.toLowerCase()}`}
                                    >
                                        <PopupMenuItemLargeIcon>
                                            <Icon>{tool.icon}</Icon>
                                        </PopupMenuItemLargeIcon>

                                        <strong>
                                            <span>{tool.name}</span>
                                            {tool.isAI && <AiTag>AI</AiTag>}
                                            {tool.isNew && <NewTag>New</NewTag>}
                                            {tool.isEarlyAccess && <EarlyAccessTag>Early Access</EarlyAccessTag>}
                                        </strong>
                                        <span>{tool.description}</span>
                                    </PopupMenuItemLarge>
                                );
                            })}
                        </ToolDropdownPortal>
                    </MenuItem>

                    {!isMenuHidden && <SeparatorMenu />}
                </>
            )}

            {!isMenuHidden && userInfo?.userID && userInfo.userID > 0 && (
                <>
                    <MenuItem onClick={(e) => e.nativeEvent.stopImmediatePropagation()}>
                        <MenuItemStrong
                            onClick={() => {
                                closeAll();
                                setIsSocialMenuVisible(!isSocialMenuVisible);
                            }}
                        >
                            {T._('Follow us', 'Admin')}
                        </MenuItemStrong>
                        <MenuItemIcon
                            onClick={() => {
                                closeAll();
                                setIsSocialMenuVisible(!isSocialMenuVisible);
                            }}
                            $isDark={true}
                        >
                            <AngleDown />
                        </MenuItemIcon>
                        <SocialMenu isSocialPopupVisible={isSocialMenuVisible} />
                    </MenuItem>

                    <SeparatorMenu />

                    <MenuItemStrong onClick={() => goToBlog()}>{T._('Blog', 'Admin')}</MenuItemStrong>
                </>
            )}
        </MenuLeft>
    );
};

export default LeftMenu;
